import { FC, useEffect, useRef, useState } from "react";
import { getPulseStream } from "@/controllers/pulse";
import { PulseItem } from "@/models/pulse";
import styles from "./MarketsPulse.module.scss";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { CATEGORY_META, clockTime, oneLockedTeaser } from "./utils";

const POLL_MS = 60_000;
const MAX_ITEMS = 40;

/** Newest data events first, polled every minute; new arrivals flash in at the left. */
const PulseTape: FC<{ universeId: number; onOpen: (item: PulseItem) => void }> = ({ universeId, onOpen }) => {
    const [items, setItems] = useState<PulseItem[]>([]);
    const [fresh, setFresh] = useState<Set<string>>(new Set());
    const cursor = useRef<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        cursor.current = null;
        setItems([]);
        let timer: ReturnType<typeof setTimeout>;

        const load = async (initial: boolean) => {
            try {
                const data = await getPulseStream({ since: initial ? undefined : cursor.current || undefined, limit: MAX_ITEMS, universeId }, controller.signal);
                if (data.rows.length) {
                    cursor.current = data.cursor;
                    setItems((current) => {
                        const seen = new Set(current.map((item) => item.id));
                        const added = data.rows.filter((item) => !seen.has(item.id));
                        if (!initial) setFresh(new Set(added.map((item) => item.id)));
                        return oneLockedTeaser([...added, ...current]).slice(0, MAX_ITEMS);
                    });
                }
            } catch {
                // A failed poll keeps the tape as it was; the next one retries.
            }
            timer = setTimeout(() => load(false), POLL_MS);
        };
        load(true);
        return () => {
            controller.abort();
            clearTimeout(timer);
        };
    }, [universeId]);

    return (
        <section className={styles.tape} aria-label="Pulse: latest data events">
            <div className={styles.tapeLabel}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                    <path d="M2 12h4l3-8 4 16 3-8h6" />
                </svg>
                PULSE
            </div>
            <div className={styles.tapeTrack}>
                {items.length === 0 ? (
                    <span className={`${styles.pulse} ${styles.faint}`}>Waiting for the first events of the day…</span>
                ) : (
                    // Two copies side by side; the strip slides left by one copy's width and loops.
                    <div className={styles.marquee} style={{ animationDuration: `${Math.max(45, items.length * 6)}s` }}>
                        {[0, 1].map((copy) =>
                            items.map((item) => (
                                <button key={`${copy}-${item.id}`} className={`${styles.pulse} ${fresh.has(item.id) ? styles.fresh : ""}`} onClick={() => onOpen(item)} aria-hidden={copy === 1} tabIndex={copy === 1 ? -1 : 0}>
                                    <span className={`${styles.tag} ${styles[`tag-${item.category}`]}`}>{CATEGORY_META[item.category]?.tag}</span>
                                    {item.ticker && <span className={styles.ticker}>{item.ticker}</span>}
                                    {item.locked && <LockOutlinedIcon className={styles.lockIcon} />}
                                    <span>{item.locked ? "Alt data outlier · ORTEX Advanced" : item.headline}</span>
                                    <span className={`${styles.faint} ${styles.num}`}>{clockTime(item.timestamp)}</span>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default PulseTape;
